import React, { useEffect, useRef, useState } from "react";
import * as d3 from "d3";
import "./css/Visualization5.css";

const Visualization5 = () => {
  const svgRef = useRef();
  const [data, setData] = useState([]);
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [yearRange, setYearRange] = useState([0, 0]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [availableCountries, setAvailableCountries] = useState([]);
  const [validYears, setValidYears] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
  };

  useEffect(() => {
    d3.csv(`${process.env.PUBLIC_URL}/data/co-emissions-per-capita.csv`).then((fetchedData) => {
      const parsedData = fetchedData
        .map((d) => ({
          Entity: d.Entity ? d.Entity.trim() : "",
          Year: +d.Year,
          CO2PerCapita: +d["Annual CO₂ emissions (per capita)"] || 0,
        }))
        .filter((d) => d.Entity !== "" && d.Year >= 1970); // Exclude entries before 1970
  
      setData(parsedData);
  
      // Extract and set valid years (1970 and later)
      const yearsWithData = Array.from(new Set(parsedData.map((d) => d.Year)));
      const sortedYears = yearsWithData.sort((a, b) => a - b);
      setValidYears(sortedYears);
      setYearRange([d3.min(sortedYears), d3.max(sortedYears)]);
  
      // Default year logic
      const defaultYear = yearsWithData.includes(2023) ? 2023 : d3.min(sortedYears);
      setSelectedYear(defaultYear);
  
      // Default countries logic (pick any 3 for the default year)
      const countriesForDefaultYear = Array.from(
        new Set(
          parsedData.filter((d) => d.Year === defaultYear).map((d) => d.Entity)
        )
      ).sort();
      setAvailableCountries(countriesForDefaultYear);
  
      const defaultCountries = countriesForDefaultYear.slice(0, 3); // Pick the first 3 countries
      setSelectedCountries(defaultCountries);
    });
  }, []);  

  useEffect(() => {
    if (!data.length || selectedYear === null) return;

    const countries = Array.from(
      new Set(data.filter((d) => d.Year === selectedYear).map((d) => d.Entity))
    ).sort();

    setAvailableCountries(countries);

    setSelectedCountries((prevSelected) =>
      prevSelected.filter((country) => countries.includes(country))
    );
  }, [data, selectedYear]);

  useEffect(() => {
    if (!data.length || selectedYear === null) return;

    const width = 1000;
    const height = 600;
    const margin = { top: 70, right: 50, bottom: 120, left: 100 };

    d3.select(svgRef.current).selectAll("*").remove();

    const svg = d3
      .select(svgRef.current)
      .attr("width", width)
      .attr("height", height);

    const filteredData =
      selectedCountries.length === 0
        ? []
        : data.filter(
            (d) => d.Year === selectedYear && selectedCountries.includes(d.Entity)
          );

    if (!filteredData.length) {
      svg
        .append("text")
        .attr("x", width / 2)
        .attr("y", height / 2)
        .attr("text-anchor", "middle")
        .attr("class", "no-data-message")
        .text("No data available for the selected year and countries");
      return;
    }

    const xScale = d3
      .scaleBand()
      .domain(filteredData.map((d) => d.Entity))
      .range([margin.left, width - margin.right])
      .padding(0.2);

    const yScale = d3
      .scaleLinear()
      .domain([0, d3.max(filteredData, (d) => d.CO2PerCapita)])
      .nice()
      .range([height - margin.bottom, margin.top]);

    const defs = svg.append("defs");
    defs
      .append("linearGradient")
      .attr("id", "barGradient")
      .attr("x1", "0%")
      .attr("y1", "0%")
      .attr("x2", "0%")
      .attr("y2", "100%")
      .selectAll("stop")
      .data([
        { offset: "0%", color: "#69b3a2" },
        { offset: "100%", color: "#404080" },
      ])
      .enter()
      .append("stop")
      .attr("offset", (d) => d.offset)
      .attr("stop-color", (d) => d.color);

    svg
      .append("g")
      .attr("transform", `translate(0,${height - margin.bottom})`)
      .call(d3.axisBottom(xScale).tickSizeOuter(0))
      .selectAll("text")
      .attr("transform", "rotate(-45)")
      .style("text-anchor", "end")
      .style("font-size", "12px");

    svg
      .append("g")
      .attr("transform", `translate(${margin.left},0)`)
      .call(d3.axisLeft(yScale))
      .selectAll("text")
      .style("font-size", "12px");

    const tooltip = d3
      .select(".tooltip")
      .style("opacity", 0)
      .style("position", "absolute")
      .style("pointer-events", "none");

    svg
      .selectAll(".bar")
      .data(filteredData)
      .join("rect")
      .attr("class", "bar")
      .attr("x", (d) => xScale(d.Entity))
      .attr("y", (d) => yScale(d.CO2PerCapita))
      .attr("width", xScale.bandwidth())
      .attr("height", (d) => height - margin.bottom - yScale(d.CO2PerCapita))
      .attr("fill", "url(#barGradient)")
      .on("mousemove", (event, d) => {
        tooltip
          .html(
            `<strong>Country:</strong> ${d.Entity}<br/>
             <strong>Year:</strong> ${d.Year}<br/>
             <strong>CO₂ Per Capita:</strong> ${d.CO2PerCapita.toFixed(2)}`
          )
          .style("left", event.pageX + 10 + "px")
          .style("top", event.pageY + "px")
          .style("opacity", 1);
      })
      .on("mouseleave", () => {
        tooltip.style("opacity", 0);
      });

    svg
      .selectAll(".bar-label")
      .data(filteredData)
      .join("text")
      .attr("class", "bar-label")
      .attr("x", (d) => xScale(d.Entity) + xScale.bandwidth() / 2)
      .attr("y", (d) => yScale(d.CO2PerCapita) - 5)
      .attr("text-anchor", "middle")
      .style("font-size", "12px")
      .style("fill", "#333")
      .text((d) => d.CO2PerCapita.toFixed(2));
  }, [data, selectedYear, selectedCountries]);

  const handleSliderChange = (e) => {
    const targetYear = +e.target.value;
    const closestYear = validYears.find((y) => y >= targetYear);
    setSelectedYear(closestYear || validYears[validYears.length - 1]);
  };

  const handleCountryChange = (e) => {
    const value = e.target.value;
    if (value === "selectAll") {
      setSelectedCountries([...availableCountries]);
    } else if (value === "clearAll") {
      setSelectedCountries([]);
    } else if (selectedCountries.includes(value)) {
      setSelectedCountries(selectedCountries.filter((c) => c !== value));
    } else {
      setSelectedCountries([...selectedCountries, value]);
    }
  };

  return (
    <div className="visualization-layout">
      {/* Tooltip for hover information */}
      <div className="tooltip"></div>
      <div className="header-container">
        <h2>CO₂ Emissions Per Capita</h2>
        <p>
          This visualization displays the per capita CO₂ emissions for selected countries across 
          various years. Use the slider and filter options to explore the data.
        </p>
      </div>
      <div className="right-column">
        <div className="visualization-container">
          {/* Button to open filter modal */}
          <button className="open-modal-btn" onClick={toggleModal}>
            Filter Countries
          </button>
          {/* SVG Visualization */}
          <svg ref={svgRef}></svg>
        </div>
      </div>
  
      {/* Modal for filtering countries */}
      {isModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Filter Countries</h3>
            <div className="controls">
              <label>Select Year: </label>
              <input
                type="range"
                min={yearRange[0]}
                max={yearRange[1]}
                value={selectedYear || ""}
                onChange={handleSliderChange}
              />
              <span>{selectedYear}</span>
            </div>
            <div className="country-list">
              <div>
                <input
                  type="radio"
                  id="selectAll"
                  value="selectAll"
                  checked={selectedCountries.length === availableCountries.length}
                  onChange={handleCountryChange}
                />
                <label htmlFor="selectAll">Select All</label>
              </div>
              <div>
                <input
                  type="radio"
                  id="clearAll"
                  value="clearAll"
                  checked={selectedCountries.length === 0}
                  onChange={handleCountryChange}
                />
                <label htmlFor="clearAll">Clear All</label>
              </div>
              <div className="scrollable-country-list">
                {availableCountries.map((country) => (
                  <div key={country}>
                    <input
                      type="checkbox"
                      id={country}
                      value={country}
                      checked={selectedCountries.includes(country)}
                      onChange={handleCountryChange}
                    />
                    <label htmlFor={country}>{country}</label>
                  </div>
                ))}
              </div>
            </div>
            <button className="close-modal-btn" onClick={toggleModal}>
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );  
};

export default Visualization5;
